import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Card, Modal, Select, Space, Spin, Typography, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectPlaidAccounts } from '@/store/features/plaid/plaid-selectors';
import { fetchPlaidWealthSnapshot } from '@/store/features/plaid/plaid-thunks';
import { selectUser } from '@/store/features/userinfo/userinfoSlice';
import { assignBankRelationship, fetchBankLinkOptions, updateBankRelationship } from '@/api/custom';
import { useTheme } from '@/contexts/ThemeContext';
import { formatAccountType } from '@/util/account-type-format';
import {
  BankLinkOptionsResponse,
  BankLinkWallet,
  bankLinkPickerPath,
  connectedFundingBankLabel,
  findReuseSource,
  formatFundingBankLabel,
  isPickerManagedWallet,
  isFundingBankSlotEmptiedError,
  walletHasActiveFundingBank,
  walletNeedsBankLink,
} from '@/util/bank-link';
import {
  getAccountFundingPlaidId,
  getDepositEligiblePlaidAccounts,
} from './deposit-account-selection';
import PlaidLinkModal from './PlaidLinkModal';
import { hasPlaidRedirectQueryParams } from './plaid-redirect-utils';

const { Title, Text } = Typography;

const walletLabel = (wallet: BankLinkWallet): string => {
  const goal = formatAccountType(wallet.goal);
  return goal ? `${wallet.account_name} (${goal})` : wallet.account_name;
};

const BankLinkPicker: React.FC = () => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const requestedAccountId = searchParams.get('accountId') ?? '';
  const plaidAccounts = useAppSelector(selectPlaidAccounts);
  const user = useAppSelector(selectUser);
  const activeUserId = user?.userId || user?.cognito_id || null;
  const depositPlaidAccounts = useMemo(
    () => getDepositEligiblePlaidAccounts(plaidAccounts),
    [plaidAccounts],
  );

  const [options, setOptions] = useState<BankLinkOptionsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plaidSnapshotLoading, setPlaidSnapshotLoading] = useState(false);
  const [plaidSnapshotFailed, setPlaidSnapshotFailed] = useState(false);
  const [walletId, setWalletId] = useState(requestedAccountId);
  const [plaidId, setPlaidId] = useState('');
  const [plaidModalVisible, setPlaidModalVisible] = useState(() =>
    hasPlaidRedirectQueryParams(searchParams),
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const submittingRef = useRef(false);
  const plaidSnapshotRequestedForUserRef = useRef<string | null>(null);
  const plaidSnapshotRequestRef = useRef<{
    abort: () => void;
    unwrap: () => Promise<unknown>;
  } | null>(null);
  const plaidReturnPath = bankLinkPickerPath(requestedAccountId);

  const eligiblePlaidAccounts = useMemo(() => {
    const fundingAccountIds = options?.funding_account_ids;
    if (fundingAccountIds === undefined) {
      return depositPlaidAccounts;
    }
    const allowedFundingIds = new Set(fundingAccountIds);
    return depositPlaidAccounts.filter((account) =>
      allowedFundingIds.has(getAccountFundingPlaidId(account)),
    );
  }, [depositPlaidAccounts, options?.funding_account_ids]);
  const hasEligiblePlaidAccounts = eligiblePlaidAccounts.length > 0;

  const loadOptions = async () => {
    setLoading(true);
    setError(null);
    try {
      const nextOptions = await fetchBankLinkOptions();
      setOptions(nextOptions);
    } catch (loadError) {
      console.error('Failed to load bank link options', loadError);
      setOptions(null);
      setError('We could not load your wallets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const requestPlaidSnapshot = useCallback(
    (userId: string) => {
      const previousRequest = plaidSnapshotRequestRef.current;
      plaidSnapshotRequestRef.current = null;
      previousRequest?.abort();

      plaidSnapshotRequestedForUserRef.current = userId;
      setPlaidSnapshotFailed(false);
      setPlaidSnapshotLoading(true);

      const request = dispatch(fetchPlaidWealthSnapshot({ range: 'ALL' }));
      plaidSnapshotRequestRef.current = request;
      void request
        .unwrap()
        .catch(() => {
          if (plaidSnapshotRequestRef.current === request) {
            plaidSnapshotRequestedForUserRef.current = null;
            setPlaidSnapshotFailed(true);
          }
        })
        .finally(() => {
          if (plaidSnapshotRequestRef.current === request) {
            setPlaidSnapshotLoading(false);
          }
        });
    },
    [dispatch],
  );

  useEffect(() => {
    void loadOptions();
  }, []);

  useEffect(() => {
    if (
      !activeUserId ||
      hasEligiblePlaidAccounts ||
      plaidSnapshotRequestedForUserRef.current === activeUserId
    ) {
      return;
    }

    requestPlaidSnapshot(activeUserId);
  }, [activeUserId, hasEligiblePlaidAccounts, requestPlaidSnapshot]);

  useEffect(
    () => () => {
      const request = plaidSnapshotRequestRef.current;
      plaidSnapshotRequestRef.current = null;
      request?.abort();
    },
    [],
  );

  const pickerWallets = useMemo(
    () => (options?.wallets ?? []).filter(isPickerManagedWallet),
    [options],
  );
  const requestedWallet = pickerWallets.find((wallet) => wallet.account_id === requestedAccountId);
  const lockWallet = Boolean(requestedWallet);

  useEffect(() => {
    if (pickerWallets.length === 0) {
      return;
    }
    if (lockWallet && requestedWallet) {
      setWalletId(requestedWallet.account_id);
      return;
    }
    if (walletId && pickerWallets.some((wallet) => wallet.account_id === walletId)) {
      return;
    }
    const firstNeedingLink = pickerWallets.find(walletNeedsBankLink);
    setWalletId((firstNeedingLink ?? pickerWallets[0]).account_id);
  }, [pickerWallets, walletId, lockWallet, requestedWallet]);

  const selectedWallet = pickerWallets.find((wallet) => wallet.account_id === walletId);
  const reuseSource = findReuseSource(options?.reuse_sources ?? [], plaidId);
  const pendingSource = findReuseSource(options?.pending_sources ?? [], plaidId);
  const selectedPlaidAccount = eligiblePlaidAccounts.find(
    (account) => getAccountFundingPlaidId(account) === plaidId,
  );
  const connectedBankLabel = selectedWallet
    ? connectedFundingBankLabel(selectedWallet, depositPlaidAccounts)
    : '';
  const hasActiveFundingBank = selectedWallet ? walletHasActiveFundingBank(selectedWallet) : false;
  const selectedIsCurrent = Boolean(plaidId) && selectedWallet?.current_link?.plaid_id === plaidId;
  const needsAnotherPlaidBank =
    hasActiveFundingBank &&
    eligiblePlaidAccounts.length > 0 &&
    eligiblePlaidAccounts.every(
      (account) => getAccountFundingPlaidId(account) === selectedWallet?.current_link?.plaid_id,
    );
  const connectBlocked =
    !selectedWallet || !plaidId || hasActiveFundingBank || Boolean(pendingSource) || submitting;
  const updateBlocked =
    !selectedWallet ||
    !plaidId ||
    !hasActiveFundingBank ||
    selectedIsCurrent ||
    Boolean(pendingSource) ||
    submitting;
  const oldBankLabel = connectedBankLabel || 'the current bank';
  const newBankLabel = selectedPlaidAccount
    ? formatFundingBankLabel(selectedPlaidAccount)
    : 'the selected bank';

  const handleConnect = async () => {
    if (connectBlocked || !selectedWallet || !plaidId) {
      return;
    }
    setSubmitting(true);
    try {
      const result = await assignBankRelationship(selectedWallet.account_id, plaidId);
      if (result.method === 'reused') {
        message.success('This bank was reused from another wallet. No new Plaid login was needed.');
      } else if (result.method === 'already_linked') {
        message.success('This wallet is already connected to that bank.');
      } else {
        message.success('Bank connected. Apex may take a moment to approve it.');
      }
      await loadOptions();
    } catch (assignError) {
      console.error('Failed to assign bank relationship', assignError);
      const assignMessage =
        assignError instanceof Error && assignError.message
          ? assignError.message
          : 'We could not connect that bank to this wallet. Please try again.';
      message.error(assignMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdate = async () => {
    if (
      !selectedWallet ||
      !plaidId ||
      !hasActiveFundingBank ||
      selectedIsCurrent ||
      Boolean(pendingSource) ||
      submittingRef.current
    ) {
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    try {
      const result = await updateBankRelationship(selectedWallet.account_id, plaidId);
      if (result.method === 'reused') {
        message.success('Funding bank updated. This bank was reused from another wallet.');
      } else {
        message.success('Funding bank updated. Apex may take a moment to approve it.');
      }
      setPlaidId('');
      setConfirmOpen(false);
      await loadOptions();
    } catch (updateError) {
      console.error('Failed to update bank relationship', updateError);
      const updateMessage =
        updateError instanceof Error && updateError.message
          ? updateError.message
          : 'We could not update the funding bank. Please try again.';
      message.error(updateMessage);
      if (isFundingBankSlotEmptiedError(updateMessage)) {
        setConfirmOpen(false);
      }
      await loadOptions();
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const containerClass = `transfer-deposit-container ${theme}-theme`;
  const pageTitle = requestedWallet
    ? hasActiveFundingBank
      ? `Change funding bank for ${walletLabel(requestedWallet)}`
      : `Choose a bank for ${walletLabel(requestedWallet)}`
    : hasActiveFundingBank
      ? 'Change funding bank'
      : 'Choose a bank for your wallet';
  const pageSubtitle = hasActiveFundingBank
    ? 'Pick a different linked bank. We will switch this wallet from the current bank to the one you pick.'
    : requestedWallet
      ? 'Pick one linked bank for this brokerage wallet. The same bank can be reused on more than one wallet.'
      : 'Pick one linked bank for each new brokerage wallet. The same bank can be reused on more than one wallet.';

  if (loading) {
    return (
      <div className={containerClass} style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className={containerClass} style={{ padding: 24, maxWidth: 800, margin: '0 auto' }}>
      <Card>
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
          <div>
            <Title level={3} style={{ marginBottom: 8 }}>
              {pageTitle}
            </Title>
            <Text type="secondary">{pageSubtitle}</Text>
          </div>

          {error && <Alert type="error" showIcon message={error} />}

          {options && pickerWallets.length === 0 && (
            <Alert
              type="info"
              showIcon
              message="No new wallets need a bank pick"
              description="Existing wallets keep their current bank links. Open a new brokerage account to choose a bank here."
            />
          )}

          {pickerWallets.length > 0 && eligiblePlaidAccounts.length === 0 && (
            <Alert
              type="warning"
              showIcon
              message="Link a checking or savings account first"
              action={
                <Space>
                  {plaidSnapshotFailed && (
                    <Button
                      onClick={() => activeUserId && requestPlaidSnapshot(activeUserId)}
                      loading={plaidSnapshotLoading}
                      disabled={!activeUserId || plaidSnapshotLoading}
                    >
                      Try again
                    </Button>
                  )}
                  <Button type="primary" onClick={() => setPlaidModalVisible(true)}>
                    Link External Account
                  </Button>
                </Space>
              }
            />
          )}

          {needsAnotherPlaidBank && (
            <Alert
              type="warning"
              showIcon
              message="Link another checking or savings account first"
              description="This wallet already uses your only linked bank. Link another account, then pick it here to update."
              action={
                <Button type="primary" onClick={() => setPlaidModalVisible(true)}>
                  Link External Account
                </Button>
              }
            />
          )}

          {pickerWallets.length > 0 && (
            <>
              <div>
                <Text strong>Brokerage wallet</Text>
                {lockWallet && selectedWallet ? (
                  <div style={{ marginTop: 8 }}>
                    <Text>{walletLabel(selectedWallet)}</Text>
                  </div>
                ) : (
                  <Select
                    aria-label="Brokerage wallet"
                    style={{ width: '100%', marginTop: 8 }}
                    value={walletId || undefined}
                    placeholder="Select a wallet"
                    onChange={setWalletId}
                    disabled={submitting}
                    options={pickerWallets.map((wallet) => ({
                      value: wallet.account_id,
                      label: walletLabel(wallet),
                    }))}
                  />
                )}
              </div>

              {selectedWallet?.current_link && (
                <Alert
                  type="success"
                  showIcon
                  message={
                    connectedBankLabel
                      ? `This wallet is connected to ${connectedBankLabel}${
                          selectedWallet.current_link.state
                            ? ` (${selectedWallet.current_link.state})`
                            : ''
                        }`
                      : `This wallet is connected${
                          selectedWallet.current_link.state
                            ? ` (${selectedWallet.current_link.state})`
                            : ''
                        }`
                  }
                />
              )}

              <div>
                <Text strong>Bank account</Text>
                <Select
                  aria-label="Bank account"
                  style={{ width: '100%', marginTop: 8 }}
                  value={plaidId || undefined}
                  placeholder="Select a linked bank"
                  onChange={setPlaidId}
                  disabled={eligiblePlaidAccounts.length === 0 || submitting}
                  options={eligiblePlaidAccounts.map((account) => ({
                    value: getAccountFundingPlaidId(account),
                    label: formatFundingBankLabel(account),
                  }))}
                />
              </div>

              {selectedIsCurrent && (
                <Alert
                  type="info"
                  showIcon
                  message="This wallet is already connected to that bank. Pick a different one to update."
                />
              )}

              {pendingSource && !selectedIsCurrent && (
                <Alert
                  type="warning"
                  showIcon
                  message={`Apex is still approving this bank on ${pendingSource.source_account_name}. Wait until that finishes, then you can reuse it here.`}
                />
              )}

              {reuseSource && selectedPlaidAccount && !selectedIsCurrent && (
                <Alert
                  type="info"
                  showIcon
                  message={`We'll reuse ${formatFundingBankLabel(selectedPlaidAccount)} from another wallet. Apex will not ask you to verify Plaid again.`}
                />
              )}

              <Space>
                {hasActiveFundingBank ? (
                  <Button
                    type="primary"
                    disabled={updateBlocked}
                    onClick={() => setConfirmOpen(true)}
                  >
                    Update funding bank
                  </Button>
                ) : (
                  <Button
                    type="primary"
                    loading={submitting}
                    disabled={connectBlocked}
                    onClick={() => void handleConnect()}
                  >
                    Connect bank
                  </Button>
                )}
                <Button onClick={() => navigate('/home/wealth')} disabled={submitting}>
                  Done
                </Button>
              </Space>
            </>
          )}
        </Space>
      </Card>
      <Modal
        className="funding-bank-replace-confirm"
        title="Replace funding bank?"
        open={confirmOpen}
        onOk={() => handleUpdate()}
        onCancel={() => {
          if (!submitting) {
            setConfirmOpen(false);
          }
        }}
        okText="Update funding bank"
        confirmLoading={submitting}
        cancelText="Cancel"
        cancelButtonProps={{ disabled: submitting }}
        maskClosable={!submitting}
        closable={!submitting}
        keyboard={!submitting}
        destroyOnClose
      >
        <p>
          This wallet is funded from {oldBankLabel}
          {selectedWallet?.current_link?.state === 'PENDING' ? ' (still pending)' : ''}. We&apos;ll
          switch it to {newBankLabel}.
        </p>
      </Modal>
      <PlaidLinkModal
        visible={plaidModalVisible}
        returnPath={plaidReturnPath}
        onClose={() => setPlaidModalVisible(false)}
        onSuccess={() => {
          setPlaidModalVisible(false);
          void loadOptions();
        }}
      />
    </div>
  );
};

export default BankLinkPicker;
